<script lang="ts">
  import { keyboard } from '../keys/keyboard.svelte';
  import type { PrStore } from '../state/pr.svelte';
  import { prPath, type PrTab } from '../router';
  import { carryFilter } from '../logic/filefilter';
  import { count } from '../logic/browse';
  import { session } from '../state/session.svelte';
  import { prefs } from '../state/prefs.svelte';
  import { applyTheme, nextTheme, themeTitle } from '../theme';
  import { nav } from '../nav.svelte';
  import { feedbackUi } from '../feedback/feedback.svelte';
  import { jumpUi } from '../jump/jump.svelte';
  import { locationOf, type Here, type LocationPart } from '../logic/location';
  import { truncatedTitle } from '../logic/truncatedTitle';
  import EarlyAccess from './EarlyAccess.svelte';
  import Popover from './Popover.svelte';
  import { flushSync } from 'svelte';
  import { copyText } from '../clipboard';
  import { toast } from '../state/toast.svelte';

  interface Props {
    pr?: PrStore;
    tab?: PrTab;
    /** The whole pull request's review progress (base → head), whatever comparison is shown. */
    progress?: { done: number; total: number; filtered?: boolean } | null;
    /** Some of the pull request's files are still being counted: the figure is a count so far. */
    counting?: boolean;
    /** The page's subject as it knows it (a history it resolved, a compare's base as read), for the location line; a pull request's comes from `pr`. */
    here?: Here | null;
  }
  let { pr, tab, progress = null, counting = false, here = null }: Props = $props();

  const JUMP = { kind: 'open', menu: 'jump' } as const;

  function cycleTheme() {
    const next = nextTheme(prefs.value.theme);
    prefs.set('theme', next);
    applyTheme(next);
  }

  const filesCount = $derived(pr?.pr?.changedFiles ?? pr?.files?.length ?? null);
  const commitsCount = $derived(pr ? pr.snapshots.length : null);
  const ghUrl = $derived(pr?.pr?.url ?? null);
  /** The owner's policy page (an organization's, or a personal account's fixed one), read through this repository. */
  const policy = $derived(
    pr?.repository
      ? { owner: pr.repository.owner.login, href: `/settings/orgs/${encodeURIComponent(pr.repository.owner.login)}?repo=${encodeURIComponent(pr.repo)}` }
      : null,
  );
  /** The pull request's tabs, in order. */
  const tabs = $derived.by(() => {
    if (!pr) return [];
    const href = (t: PrTab) => carryFilter(prPath(pr.owner, pr.repo, pr.number, t), nav.query);
    return [
      { tab: 'overview' as PrTab, href: href('overview'), label: 'Overview', count: null, title: undefined },
      { tab: 'files' as PrTab, href: href('files'), label: 'Files', count: filesCount, title: undefined },
      { tab: 'commits' as PrTab, href: href('commits'), label: 'Snapshots', count: commitsCount || null, title: undefined },
      // One place for the viewer's unsent drafts, sending them, and what was said in review.
      {
        tab: 'review' as PrTab,
        href: href('review'),
        label: 'Review',
        count: pr.draftCount ? `(${pr.draftCount} unsent)` : null,
        title: pr.draftCount ? `${pr.draftCount} unsent, ${pr.includedCount} of them ticked to send` : 'Your unsent drafts, sending your review, and the review conversation',
      },
    ];
  });

  /** How many tabs, from the end, do not fit in the bar and are in the "More" menu instead (never clipped). */
  let overflow = $state(0);
  let navEl: HTMLElement | null = $state(null);
  let railEl: HTMLElement | null = $state(null);
  let locEl: HTMLElement | null = $state(null);
  let moreEl: HTMLButtonElement | null = $state(null);
  let moreOpen = $state(false);
  const shown = $derived(tabs.slice(0, tabs.length - overflow));
  const folded = $derived(tabs.slice(tabs.length - overflow));
  const foldedCurrent = $derived(folded.find((t) => t.tab === tab) ?? null);

  /** Moves tabs into the menu, last first, until the rest fit. */
  function fit() {
    if (!navEl) return;
    overflow = 0;
    flushSync();
    while (navEl.scrollWidth > navEl.clientWidth + 1 && overflow < tabs.length) {
      overflow++;
      flushSync();
    }
    navEl.scrollLeft = 0;
  }
  $effect(() => {
    // Again when a tab's words change (its counts) or the bar's width does.
    // (flushSync cannot run inside an effect: it fits on the next frame, and the observer first fits before the bar is painted.)
    void tabs.map((t) => `${t.label}${t.count ?? ''}`).join();
    const frame = requestAnimationFrame(fit);
    if (!railEl || !navEl) return () => cancelAnimationFrame(frame);
    // The bar's width, and what is beside the tabs (the wordmark, the controls and the progress, which fill in later):
    // never the tabs themselves, which fitting resizes (that would be a ResizeObserver loop), nor the location, which
    // takes only the room the tabs leave (layout.css), so its resize is the fit's own doing and would fit them twice.
    const ro = new ResizeObserver(() => fit());
    ro.observe(railEl);
    for (const el of railEl.children) if (el !== navEl && el !== locEl) ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  });

  // ---------------------------------------------------------------- the location line (logic/location.ts)
  const location = $derived(
    locationOf(nav.route, nav.query, pr ? { subject: { kind: 'pull', owner: pr.owner, repo: pr.repo, number: pr.number }, title: pr.pr?.title ?? null } : here),
  );
  /**
   * The parts above the subject, in a menu where the location has room for
   * fewer of them (layout.css, by the location's own width). The menu holds
   * them all, so it is right whichever the bar left out, and its button is
   * named for them.
   */
  const up = $derived(location ? location.parts.slice(0, -1) : []);
  const words = (p: LocationPart) => [p.lead, p.label].filter(Boolean).join(' ');
  let locMoreEl: HTMLButtonElement | null = $state(null);
  let locMoreOpen = $state(false);
  function closeLocMore(refocus: boolean) {
    locMoreOpen = false;
    if (refocus) locMoreEl?.focus();
  }

  /** The key last copied, shown as copied until the pointer or the focus leaves the button. */
  let copied: string | null = $state(null);
  /** What the polite region says: each copy is a new text node (`said.n`), so a second copy is said again. */
  let said = $state({ text: '', n: 0 });
  async function copyKey(key: string) {
    if (await copyText(key)) {
      copied = key;
      said = { text: `Copied ${key}`, n: said.n + 1 };
      return;
    }
    copied = null;
    const refused = `Couldn't copy the subject key ${key}: the browser refused the clipboard`;
    said = { text: refused, n: said.n + 1 };
    // Seen as well: the button looks as it did, so nothing else says the key was not copied.
    toast.show({ text: refused });
  }

  function closeMore(refocus: boolean) {
    moreOpen = false;
    if (refocus) moreEl?.focus();
  }

  let signingOut = $state(false);
  async function signOut() {
    signingOut = true;
    // On success the page navigates away; otherwise the user is still signed in.
    if (!(await session.signOut())) signingOut = false;
  }
</script>

<header class="rail topbar" bind:this={railEl}>
  <!-- On a page with a location, the top bar's one way to the dashboard: named for it, keeping its words (label in name). -->
  <a class="brand" href="/" title="Dashboard" aria-label="Changeling / review: Dashboard">Changeling<span>&nbsp;/ review</span></a>
  <EarlyAccess bar />
  {#if location}
    <!-- Where the page is: owner / repo / subject, each a link up, the last the page itself (logic/location.ts). Where it has room for fewer, the rest are in a menu. -->
    <nav class="location" aria-label="Location" bind:this={locEl}>
      <ol>
        {#if up.length}
          <li class="loc-more">
            <button
              bind:this={locMoreEl}
              type="button"
              aria-haspopup="menu"
              aria-expanded={locMoreOpen}
              aria-label="Show location: {up.map(words).join(' / ')}"
              title={up.map(words).join(' / ')}
              onclick={() => (locMoreOpen = !locMoreOpen)}>…</button
            >
          </li>
        {/if}
        {#each location.parts as p, i (i)}
          {@const last = i === location.parts.length - 1}
          <li class="loc-part" class:loc-subject={last}>
            {#if i}<span class="loc-sep" aria-hidden="true">{p.sep}</span>{/if}
            <a href={p.href} aria-current={last ? location.current : undefined} use:truncatedTitle={{ text: words(p), target: '.loc-label' }}
              >{#if p.lead}<b class="loc-lead">{p.lead}</b>{#if p.label}{' '}{/if}{/if}<span class="loc-label">{p.label}</span></a
            >
            {#if last && location.key}
              {@const key = location.key}
              <button
                type="button"
                class="loc-copy"
                aria-label="Copy the subject key"
                title="Copy the subject key, {key}: what an agent takes in changeling_connect"
                onclick={() => void copyKey(key)}
                onpointerleave={() => (copied = null)}
                onblur={() => (copied = null)}
                >{#if copied === key}<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path fill="currentColor" d="M6 10.6 3.2 7.8 2.1 8.9 6 12.8l8-8-1.1-1.1z" /></svg
                  >{:else}<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"
                    ><path fill="currentColor" d="M5 1.5h7.5A1.5 1.5 0 0 1 14 3v8.5h-1.5V3H5zM3.5 4.5h6A1.5 1.5 0 0 1 11 6v7.5A1.5 1.5 0 0 1 9.5 15h-6A1.5 1.5 0 0 1 2 13.5V6a1.5 1.5 0 0 1 1.5-1.5Zm0 1.5v7.5h6V6z" /></svg
                  >{/if}</button
              >
            {/if}
          </li>
        {/each}
      </ol>
      <!-- Polite, and in the page before anything is copied, so what a copy says is heard. -->
      <span class="sr-only loc-said" role="status">{#key said.n}<span>{said.text}</span>{/key}</span>
    </nav>
    {#if locMoreOpen && locMoreEl}
      <Popover anchor={locMoreEl} role="menu" label="The whole location" onclose={closeLocMore}>
        {#each up as p (p.href)}
          <a role="menuitem" tabindex="-1" class="nav-more-item" href={p.href} onclick={() => closeLocMore(false)}>{words(p)}</a>
        {/each}
      </Popover>
    {/if}
  {/if}
  {#if pr}
    <nav aria-label="Pull request" bind:this={navEl}>
      {#each shown as t (t.tab)}
        <a class="navlink" href={t.href} aria-current={tab === t.tab ? 'page' : undefined} title={t.title}
          >{t.label}{#if t.count !== null}<em class="count">{t.count}</em>{/if}</a
        >
      {/each}
      {#if folded.length}
        <!-- The tabs that do not fit; when the current one is among them, the button is named for it. -->
        <button
          bind:this={moreEl}
          type="button"
          class="navlink nav-more"
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          aria-current={foldedCurrent ? 'page' : undefined}
          title={folded.map((t) => t.label).join(', ')}
          onclick={() => (moreOpen = !moreOpen)}>{foldedCurrent?.label ?? 'More'} <span aria-hidden="true">▾</span></button
        >
      {/if}
    </nav>
    {#if moreOpen && moreEl}
      <Popover anchor={moreEl} role="menu" label="More tabs" onclose={closeMore}>
        {#each folded as t (t.tab)}
          <a role="menuitem" tabindex="-1" class="nav-more-item" href={t.href} aria-current={tab === t.tab ? 'page' : undefined} title={t.title} onclick={() => closeMore(false)}
            >{t.label}{#if t.count !== null}<em>{t.count}</em>{/if}</a
          >
        {/each}
      </Popover>
    {/if}
  {:else if !location}
    <nav aria-label="Main">
      <a class="navlink" href="/" aria-current={nav.route.name === 'dashboard' ? 'page' : undefined}>Dashboard</a>
      <a class="navlink" href="/gh" aria-current={nav.route.name === 'repos' ? 'page' : undefined}>Repositories</a>
      {#if session.user?.operator}
        <a class="navlink" href="/feedback" aria-current={nav.route.name === 'feedback' || nav.route.name === 'feedback-report' ? 'page' : undefined}
          >Feedback reports</a
        >
      {/if}
    </nav>
  {/if}
  <div class="bottom">
    {#if progress && progress.total}
      {@const said = `${count(progress.done)} of ${count(progress.total)} lines reviewed in the whole pull request (base → head)${progress.filtered ? ', in the files the filter keeps' : ''}${counting ? ' · still counting' : ''}`}
      <span class="bar-progress" title={said}>
        <span class="progress" aria-hidden="true"><i style="width:{(progress.done / progress.total) * 100}%"></i></span>
        <small aria-label={said}>{count(progress.done)}/{count(progress.total)}{progress.filtered ? ' · filtered' : ''}</small>
      </span>
    {/if}
    {#if session.user}
      <!-- The jump bar (components/JumpBar.svelte): to any pull request, branch, compare, history or repository. -->
      <button class="theme-btn jump-btn" onclick={() => jumpUi.show()} aria-keyshortcuts="Meta+K Control+K" title="Jump to a pull request, a branch, a compare, a history or a repository{keyboard.hint(JUMP)}"
        aria-label="Jump to…"
        ><svg class="jump-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path fill="currentColor" d="M6.5 1a5.5 5.5 0 0 1 4.38 8.82l3.65 3.65-1.06 1.06-3.65-3.65A5.5 5.5 0 1 1 6.5 1Zm0 1.5a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" /></svg><span class="jump-label">Jump</span>{#if keyboard.key(JUMP)}<kbd>{keyboard.key(JUMP)}</kbd>{/if}</button
      >
    {/if}
    {#if ghUrl}<a href={ghUrl} target="_blank" rel="noreferrer">GitHub ↗</a>{/if}
    {#if policy}<a href={policy.href} title="{policy.owner} review policy">Policy</a>{/if}
    {#if session.user}
      <button class="theme-btn feedback-btn" onclick={() => feedbackUi.show()} title="Send feedback about Changeling{keyboard.hint({ kind: 'open', menu: 'feedback' })}"
        >Feedback</button
      >
    {/if}
    <button class="theme-btn" onclick={cycleTheme} aria-label={themeTitle(prefs.value.theme)} title={themeTitle(prefs.value.theme)}>◐ {prefs.value.theme}</button>
    {#if session.user}
      <a
        class="theme-btn settings-link"
        href="/settings"
        aria-label="Settings"
        aria-current={nav.route.name === 'settings' ? 'page' : undefined}
        title="Settings{keyboard.hint({ kind: 'open', menu: 'settings' })}"
        ><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M6.43 2.94L6.82 1.10L9.18 1.10L9.57 2.94L10.47 3.31L12.04 2.28L13.72 3.96L12.69 5.53L13.06 6.43L14.90 6.82L14.90 9.18L13.06 9.57L12.69 10.47L13.72 12.04L12.04 13.72L10.47 12.69L9.57 13.06L9.18 14.90L6.82 14.90L6.43 13.06L5.53 12.69L3.96 13.72L2.28 12.04L3.31 10.47L2.94 9.57L1.10 9.18L1.10 6.82L2.94 6.43L3.31 5.53L2.28 3.96L3.96 2.28L5.53 3.31ZM8 5.6a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8Z" /></svg></a
      >
      <div class="me">
        {#if session.user.avatar_url}<img class="avatar" src={session.user.avatar_url} alt="" />{/if}
        <span class="login" title="Signed in as {session.user.login}">{session.user.login}</span>
        <button class="signout" onclick={signOut} disabled={signingOut}>{signingOut ? 'Signing out…' : 'Sign out'}</button>
      </div>
    {/if}
  </div>
  {#if session.signOutError}<p class="signout-error" role="alert">{session.signOutError}</p>{/if}
  <!-- While a write or its reload is in flight (PrStore.saving). -->
  {#if pr?.saving}<div class="progress-line" role="progressbar" aria-label="Saving changes"></div>{/if}
</header>
